import dayjs from "dayjs";
import type { DashboardResponse, GetEventStatsResponse } from "../../api/api.types";

/** Generate mock daily data for the last N days (ratios match real production data) */
function generateDailyData(days: number, baseDate = dayjs()) {
  return Array.from({ length: days }, (_, i) => {
    const date = baseDate.subtract(days - 1 - i, "day").format("YYYY-MM-DD");
    const dau = Math.floor(30 + Math.random() * 40 + i * 0.5);
    const feedRequests = dau + Math.floor(Math.random() * 20);
    // Real ratios: views ≈ 3x feeds, likes ≈ 7% of feeds, shares ≈ 0.2% of feeds
    const likes = Math.floor(feedRequests * (0.05 + Math.random() * 0.04));
    const shares = Math.round(feedRequests * (0.001 + Math.random() * 0.003));
    const views = feedRequests * 3 + Math.floor(Math.random() * 30);
    return { date, dau, feedRequests, likes, shares, views };
  });
}

/** Generate previous period with slightly lower numbers */
function generatePrevDailyData(days: number, baseDate = dayjs()) {
  const prevEnd = baseDate.subtract(days, "day");
  return Array.from({ length: days }, (_, i) => {
    const date = prevEnd.subtract(days - 1 - i, "day").format("YYYY-MM-DD");
    const dau = Math.floor(22 + Math.random() * 30 + i * 0.3);
    const feedRequests = dau + Math.floor(Math.random() * 15);
    const likes = Math.floor(feedRequests * (0.04 + Math.random() * 0.03));
    const shares = Math.round(feedRequests * (0.001 + Math.random() * 0.002));
    const views = feedRequests * 3 + Math.floor(Math.random() * 20);
    return { date, dau, feedRequests, likes, shares, views };
  });
}

export function getMockStatsData(days = 30): GetEventStatsResponse {
  const daily = generateDailyData(days);
  const prevDaily = generatePrevDailyData(days);

  return {
    daily,
    totals: {
      feed_request: daily.reduce((s, d) => s + d.feedRequests, 0),
      quote_liked: daily.reduce((s, d) => s + d.likes, 0),
      quote_shared: daily.reduce((s, d) => s + d.shares, 0),
      quote_viewed: daily.reduce((s, d) => s + d.views, 0),
      user_registered: 47,
      onboarding_completed: 38,
      subscription_event: 62,
    },
    subscriptions: {
      subscriptionStarts: 28,
      trialStarts: 20,
      paidStarts: 8,
      trialConversions: 12,
      renewals: 85,
      cancellations: 9,
      expirations: 5,
      cancelReasons: {
        UNSUBSCRIBE: 5,
        BILLING_ERROR: 2,
        CUSTOMER_SUPPORT: 1,
        UNKNOWN: 1,
      },
      storeSplit: {
        APP_STORE: 72,
        PLAY_STORE: 31,
      },
    },
    customTrials: {
      started: 14,
      converted: 5,
      conversionRate: 35.7,
      avgDaysToConvert: 2.4,
      providerSplit: {
        apple: 9,
        google: 5,
      },
      storeSplit: {
        APP_STORE: 3,
        PLAY_STORE: 2,
      },
    },
    previous: {
      daily: prevDaily,
      totals: {
        feed_request: prevDaily.reduce((s, d) => s + d.feedRequests, 0),
        quote_liked: prevDaily.reduce((s, d) => s + d.likes, 0),
        quote_shared: prevDaily.reduce((s, d) => s + d.shares, 0),
        quote_viewed: prevDaily.reduce((s, d) => s + d.views, 0),
        user_registered: 35,
        onboarding_completed: 28,
        subscription_event: 48,
      },
      subscriptions: {
        subscriptionStarts: 21,
        trialStarts: 15,
        paidStarts: 6,
        trialConversions: 8,
        renewals: 65,
        cancellations: 7,
        expirations: 4,
        cancelReasons: {
          UNSUBSCRIBE: 4,
          BILLING_ERROR: 2,
          UNKNOWN: 1,
        },
        storeSplit: {
          APP_STORE: 55,
          PLAY_STORE: 24,
        },
      },
      customTrials: {
        started: 9,
        converted: 3,
        conversionRate: 33.3,
        avgDaysToConvert: 2.8,
        providerSplit: {
          apple: 6,
          google: 3,
        },
        storeSplit: {
          APP_STORE: 2,
          PLAY_STORE: 1,
        },
      },
    },
  };
}

export const MOCK_DASHBOARD_DATA: DashboardResponse = {
  totalNewUsers: 47,
  totalProfilesCompleted: 38,
  totalPublishedQuotes: 156,
  totalQuoteViews: 2840,
  totalNewSubscriptions: 28,
  totalNewBillingCycles: 85,
  totalUnsubscribed: 9,
  totalActiveSubscribers: 312,
  totalTrialing: 45,
  totalWithAccess: 365,
  totalChurned: 28,
  totalActiveCustomTrials: 11,
  totalExpiredCustomTrials: 6,
};
